import { useState, useEffect, useRef } from 'react'
import GoldCrystal3D from './GoldCrystal3D'
import milligramLogo from '../assets/milligram.svg'
import paxgIcon from '../assets/paxg.svg'
import ethIcon from '../assets/eth.svg'
import mgIcon from '../assets/mg.svg'
import mgtvIcon from '../assets/mgtv.svg'
import usdtIcon from '../assets/usdt.svg'
import paxgLockIcon from '../assets/paxg_lock.svg'
import mgArrowsIcon from '../assets/mg_arrows.svg'
import ethLongIcon from '../assets/eth-long.svg'
import paxgLogo from '../assets/paxg-logo.png'
import uniswapLogo from '../assets/uniswap.svg'
import greywolfLogo from '../assets/greywolf.png'

const NAV_SECTIONS = [
  { id: 'hero',     label: 'Home' },
  { id: 'paxg',     label: 'PAXG' },
  { id: 'how',      label: 'How' },
  { id: 'platform', label: 'Platform' },
  { id: 'tokens',   label: 'Tokens' },
  { id: 'why',      label: 'Why Gold' },
  { id: 'custody',  label: 'Custody' },
]

const Landing = ({ onEnter }) => {
  const [active, setActive] = useState('hero')
  const landRef = useRef(null)
  const carouselRef = useRef(null)

  /* ---- Intersection observers ---- */
  useEffect(() => {
    const root = landRef.current
    if (!root) return

    // 1. Active section tracker (sidenav)
    const targets = NAV_SECTIONS.map(s => root.querySelector(`#${s.id}`)).filter(Boolean)
    const navObs = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActive(entry.target.id)
        })
      },
      { root: null, rootMargin: '-40% 0px -40% 0px', threshold: 0 }
    )
    targets.forEach(el => navObs.observe(el))

    // 2. Scroll-reveal
    const reveals = root.querySelectorAll('.land__reveal')
    const revealObs = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('land__revealed')
            revealObs.unobserve(entry.target)
          }
        })
      },
      { root: null, threshold: 0.15 }
    )
    reveals.forEach(el => revealObs.observe(el))

    // 3. Slide-in/out trigger (observe wrapper, toggle class on content)
    const slideObs = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const content = entry.target.querySelector('.land__slide-content')
          if (!content) return
          if (entry.isIntersecting) {
            content.classList.remove('land__slide-exit')
            content.classList.add('land__slide-visible')
          } else {
            content.classList.remove('land__slide-visible')
            if (entry.boundingClientRect.bottom < 0) {
              content.classList.add('land__slide-exit')
            } else {
              content.classList.remove('land__slide-exit')
            }
          }
        })
      },
      { root: null, threshold: 0 }
    )
    const slideTargets = root.querySelectorAll('.land__slide-wrap')
    slideTargets.forEach(el => slideObs.observe(el))

    // 4. Carousel — scroll triggers step changes, CSS handles the animation
    let prevStep = -1
    const handleCarouselScroll = () => {
      const wrap = carouselRef.current
      if (!wrap) return
      const rect = wrap.getBoundingClientRect()
      const vh = window.innerHeight
      const progress = Math.max(0, Math.min(1, -rect.top / (rect.height - vh)))
      const step = Math.min(2, Math.floor(progress * 3))

      if (step === prevStep) return
      prevStep = step

      const cards = wrap.querySelectorAll('.land__carousel-card')
      cards.forEach((card, i) => {
        card.classList.toggle('land__carousel-card--active', i === step)
      })

      const dots = wrap.querySelectorAll('.land__carousel-dot')
      dots.forEach((dot, i) => {
        dot.classList.toggle('land__carousel-dot--active', i === step)
      })
    }

    window.addEventListener('scroll', handleCarouselScroll, { passive: true })
    handleCarouselScroll()

    return () => {
      navObs.disconnect(); revealObs.disconnect(); slideObs.disconnect()
      window.removeEventListener('scroll', handleCarouselScroll)
    }
  }, [])

  const scrollTo = (id) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="land" ref={landRef}>

      <div className="land__glow" />

      {/* Side mini-nav */}
      <nav className="land__sidenav">
        {NAV_SECTIONS.map(s => (
          <button
            key={s.id}
            className={`land__sidenav-item${active === s.id ? ' land__sidenav-item--active' : ''}`}
            onClick={() => scrollTo(s.id)}
          >
            <span className="land__sidenav-label">{s.label}</span>
            <span className="land__sidenav-dot" />
          </button>
        ))}
      </nav>

      {/* Hero */}
      <section id="hero" className="land__hero">
        <img src={milligramLogo} alt="Milligram" className="land__brand" />
        <GoldCrystal3D />

        <h1 className="land__h1">Tokenized Micro Gold Units</h1>
        <p className="land__tagline">
          1 MG = 1 milligram of real gold. Backed by PAXG, settled on Ethereum.
        </p>

        <button className="land__cta" onClick={onEnter}>
          Launch App
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </section>

      {/* Stats */}
      <section className="land__stats land__reveal">
        <div className="land__stat">
          <strong>31,103.5</strong>
          <span>MG per PAXG</span>
        </div>
        <div className="land__stat">
          <strong>~$0.16</strong>
          <span>per MG token</span>
        </div>
        <div className="land__stat">
          <strong>1:1</strong>
          <span>Gold backed</span>
        </div>
      </section>

      {/* Marquee ribbon */}
      <div className="land__marquee">
        <div className="land__marquee-track">
          {[...Array(2)].map((_, i) => (
            <div className="land__marquee-content" key={i} aria-hidden={i > 0}>
              <span>1 MG = 1 Milligram of Gold</span>
              <span className="land__marquee-dot" />
              <span>Backed by PAXG</span>
              <span className="land__marquee-dot" />
              <span>Settled on Ethereum</span>
              <span className="land__marquee-dot" />
              <span>100% Self-Custody</span>
              <span className="land__marquee-dot" />
              <span>No KYC Required</span>
              <span className="land__marquee-dot" />
              <span>Decentralized Order Book</span>
              <span className="land__marquee-dot" />
              <span>Your Keys, Your Gold</span>
              <span className="land__marquee-dot" />
              <span>5,000 Years of Value</span>
              <span className="land__marquee-dot" />
            </div>
          ))}
        </div>
      </div>

      {/* What is PAXG — horizontal scroll slide */}
      <div id="paxg" className="land__band land__band--dark land__slide-wrap">
        <section className="land__section land__paxg land__slide-content">
          <div className="land__paxg-logo">
            <img src={paxgIcon} alt="PAXG" />
          </div>
          <div className="land__paxg-content">
            <h2 className="land__h2">What is PAXG?</h2>
            <p className="land__body">
              PAXG (Pax Gold) is an ERC-20 token issued by Paxos, where each token
              represents one fine troy ounce of real, physical gold stored in
              London LBMA vaults. It&apos;s regulated, audited monthly, and redeemable
              for physical gold bars. PAXG brings the world&apos;s oldest store of
              value onto the blockchain.
            </p>
            <div className="land__paxg-facts">
              <div className="land__paxg-fact">
                <strong>1 PAXG</strong>
                <span>= 1 Troy Ounce</span>
              </div>
              <div className="land__paxg-fact">
                <strong>LBMA Vaults</strong>
                <span>London, UK</span>
              </div>
              <div className="land__paxg-fact">
                <strong>Paxos Trust</strong>
                <span>Regulated Issuer</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Tokens showcase — MG & MGTV */}
      <div id="tokens" className="land__band land__band--lifted">
        <section className="land__showcase">
          <div className="land__showcase-item land__reveal">
            <div className="land__showcase-logo">
              <div className="land__showcase-ring" />
              <div className="land__showcase-ring land__showcase-ring--2" />
              <img src={mgIcon} alt="MG" />
            </div>
            <h3 className="land__showcase-name">MG</h3>
            <span className="land__showcase-label">Milligram Token</span>
            <p className="land__showcase-desc">
              Each MG represents exactly 1 milligram of real physical gold.
              Minted by locking PAXG in the Gold Vault at a fixed rate of
              31,103.5 MG per troy ounce. Redeemable anytime &mdash; the
              conversion is a physical constant, not an algorithm.
            </p>
            <div className="land__showcase-tags">
              <span>Gold-Backed</span>
              <span>Fixed Rate</span>
              <span>Redeemable</span>
            </div>
          </div>

          <div className="land__showcase-divider">
            <svg width="2" height="120" viewBox="0 0 2 120" fill="none">
              <line x1="1" y1="0" x2="1" y2="120" stroke="url(#divGrad)" strokeWidth="1"/>
              <defs><linearGradient id="divGrad" x1="1" y1="0" x2="1" y2="120" gradientUnits="userSpaceOnUse">
                <stop stopColor="transparent"/>
                <stop offset="0.5" stopColor="rgba(218,165,32,0.2)"/>
                <stop offset="1" stopColor="transparent"/>
              </linearGradient></defs>
            </svg>
          </div>

          <div className="land__showcase-item land__reveal" style={{transitionDelay:'0.15s'}}>
            <div className="land__showcase-logo land__showcase-logo--mgtv">
              <div className="land__showcase-ring" />
              <div className="land__showcase-ring land__showcase-ring--2" />
              <img src={mgtvIcon} alt="MGTV" />
            </div>
            <h3 className="land__showcase-name">MGTV</h3>
            <span className="land__showcase-label">Utility &amp; Governance</span>
            <p className="land__showcase-desc">
              The utility layer of the Milligram ecosystem. Pay fees with
              MGTV for a 10&times; discount on every vault, DEX, and swap
              operation. Deflationary by design &mdash; every MGTV spent
              on fees is permanently burned.
            </p>
            <div className="land__showcase-tags">
              <span>Fee Discount</span>
              <span>Deflationary</span>
              <span>Burn-on-Use</span>
            </div>
          </div>
        </section>
      </div>

      {/* How it works — curved carousel */}
      <div id="how" className="land__carousel-wrap" ref={carouselRef}>
        <div className="land__carousel-sticky">
          <h2 className="land__h2">How It Works</h2>
          <div className="land__carousel">
            <div className="land__carousel-card land__step">
              <span className="land__step-num">01</span>
              <div className="land__step-icon land__step-icon--lock">
                <img src={paxgLockIcon} alt="Lock PAXG" />
              </div>
              <div className="land__step-body">
                <h3>Lock PAXG</h3>
                <p>
                  Deposit your PAXG into the Milligram Gold Vault &mdash; a
                  non-custodial smart contract on Ethereum. Your gold stays
                  on-chain and fully under your control at all times. No
                  sign-ups, no intermediaries, no minimum deposit.
                </p>
              </div>
            </div>
            <div className="land__carousel-card land__step">
              <span className="land__step-num">02</span>
              <div className="land__step-icon land__step-icon--mint">
                <img src={mgArrowsIcon} alt="Mint MG" />
              </div>
              <div className="land__step-body">
                <h3>Mint MG</h3>
                <p>
                  Receive exactly 31,103.5 MG for every PAXG locked &mdash;
                  one token per milligram of real gold. The conversion rate is
                  a physical constant, not an algorithm. Mint and redeem
                  freely with zero slippage. The only way to mint MG is
                  through the Milligram Gold Vault smart contract.
                </p>
              </div>
            </div>
            <div className="land__carousel-card land__step">
              <span className="land__step-num">03</span>
              <div className="land__step-icon land__step-icon--use">
                <img src={mgIcon} alt="Use MG" />
              </div>
              <div className="land__step-body">
                <h3>Use Everywhere</h3>
                <p>
                  Trade MG on the built-in DEX, swap instantly via Uniswap,
                  or integrate into any dApp. Perfect for micro-payments, AI
                  agent settlement, and nano-transactions. MG has direct
                  liquidity access through smart contracts &mdash; no
                  intermediaries, no centralized exchanges. Redeem back to
                  PAXG anytime &mdash; your keys, your gold.
                </p>
              </div>
            </div>
          </div>
          <div className="land__carousel-dots">
            <span className="land__carousel-dot land__carousel-dot--active" />
            <span className="land__carousel-dot" />
            <span className="land__carousel-dot" />
          </div>
        </div>
      </div>

      {/* Platform */}
      <section id="platform" className="land__section">
        <h2 className="land__h2 land__reveal">The Platform</h2>
        <div className="land__grid">
          <div className="land__card land__reveal" style={{transitionDelay:'0s'}}>
            <div className="land__card-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M21 4H3a1 1 0 00-1 1v14a1 1 0 001 1h18a1 1 0 001-1V5a1 1 0 00-1-1z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="14" cy="12" r="2" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M2 8h20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>Gold Vault</h3>
            <p>Mint and redeem MG tokens. The conversion rate is a physical constant &mdash; not an algorithm.</p>
          </div>
          <div className="land__card land__reveal" style={{transitionDelay:'0.1s'}}>
            <div className="land__card-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>DEX</h3>
            <p>Fully decentralized order book. Set your price, place limit orders, settle peer-to-peer.</p>
          </div>
          <div className="land__card land__reveal" style={{transitionDelay:'0.2s'}}>
            <div className="land__card-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M7 16V4M7 4L3 8M7 4l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 8v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Swap</h3>
            <p>Instant swaps between ETH, USDT, and PAXG via Uniswap. No accounts, no KYC.</p>
          </div>
          <div className="land__card land__reveal" style={{transitionDelay:'0.3s'}}>
            <div className="land__card-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M13 2L3 14h9l-1 8 10-12H12l1-8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>MGTV</h3>
            <p>Utility token. 10x fee discount on every operation. Deflationary &mdash; burned on every use.</p>
          </div>
        </div>
      </section>

      {/* Why gold + Self custody — side by side */}
      <div id="why" className="land__band land__band--lifted">
        <section className="land__section land__duo">
          <div className="land__duo-col land__reveal">
            <h2 className="land__h2">Why Gold?</h2>
            <p className="land__body">
              Stablecoins depend on issuers. Crypto is volatile. Gold has held value for
              5,000 years. At ~$0.16, MG is the perfect unit for AI agent settlement,
              API micro-billing, nano-transactions &mdash; any use case where stablecoins
              are too centralized and crypto is too volatile.
            </p>
          </div>
          <div id="custody" className="land__duo-col land__reveal" style={{transitionDelay:'0.15s'}}>
            <h2 className="land__h2">Why Micro Gold Units Matter</h2>
            <p className="land__body">
              Fiat currencies lose purchasing power year after year &mdash; inflation
              is not a bug, it&apos;s a feature of central banking. Gold has held its
              value for 5,000 years, but a troy ounce costs over $3,000 &mdash; too
              expensive for everyday payments. MG solves gold&apos;s quantification
              problem by splitting it into milligram-sized units at ~$0.16 each.
              For the first time, gold is small enough for micro-payments, API billing,
              and machine-to-machine settlement &mdash; while staying 100% self-custody
              and on-chain.
            </p>
          </div>
        </section>
      </div>

      {/* Tokens */}
      <div className="land__band land__band--dark">
        <section className="land__tokens">
          {[
            { icon: mgIcon,   sym: 'MG',   name: 'Milligram',  color: '#DAA520' },
            { icon: mgtvIcon, sym: 'MGTV', name: 'Governance', color: '#B8860B' },
            { icon: paxgIcon, sym: 'PAXG', name: 'Pax Gold',   color: '#E8B830' },
            { icon: usdtIcon, sym: 'USDT', name: 'Tether',     color: '#26A17B' },
            { icon: ethIcon,  sym: 'ETH',  name: 'Ethereum',   color: '#627EEA' },
          ].map((t) => (
            <div key={t.sym} className="land__token">
              <div className="land__token-icon" style={{'--token-clr': t.color}}>
                <img src={t.icon} alt={t.sym} />
              </div>
              <div>
                <strong>{t.sym}</strong>
                <span>{t.name}</span>
              </div>
            </div>
          ))}
        </section>
      </div>

      {/* Footer */}
      <footer className="land__footer">
        <div className="land__footer-top">
          <div className="land__footer-brand">
            <img src={milligramLogo} alt="Milligram" className="land__footer-logo" />
            <p className="land__footer-tagline">Tokenized micro gold units.<br/>Backed by PAXG. Settled on Ethereum.</p>
          </div>
          <div className="land__footer-col">
            <h4>Platform</h4>
            <a href="#how">How It Works</a>
            <a href="#platform">Gold Vault</a>
            <a href="#tokens">Tokens</a>
          </div>
          <div className="land__footer-col">
            <h4>Resources</h4>
            <a href="#why">Why Gold</a>
            <a href="#custody">Micro Gold</a>
            <a href="#paxg">What is PAXG</a>
          </div>
          <div className="land__footer-col land__footer-col--logos">
            <a href="https://ethereum.org" target="_blank" rel="noopener noreferrer" className="land__footer-icon-link">
              <img src={ethLongIcon} alt="Ethereum" />
            </a>
            <a href="https://paxos.com/paxgold" target="_blank" rel="noopener noreferrer" className="land__footer-icon-link">
              <img src={paxgLogo} alt="PAXG" />
            </a>
            <a href="https://uniswap.org" target="_blank" rel="noopener noreferrer" className="land__footer-icon-link">
              <img src={uniswapLogo} alt="Uniswap" />
            </a>
          </div>
        </div>
        <div className="land__footer-bottom">
          <p className="land__footer-copy">&copy; 2006&ndash;{new Date().getFullYear()} Milligram. All rights reserved.</p>
          <p className="land__footer-credit">
            Made by
            <a href="https://greywolf.build" target="_blank" rel="noopener noreferrer">
              <img src={greywolfLogo} alt="greywolf" />
              greywolf
            </a>
          </p>
        </div>
      </footer>

    </div>
  )
}

export default Landing
